// recordsPackage.js — v238.24.0
// Client-side "instant download" records packager (Option A).
// Builds a combined, letterhead-styled PDF (cover, face sheet, per-visit
// records) with jsPDF, gathers the patient's uploaded files via signed URLs,
// and zips everything with JSZip. Returns a Blob the caller downloads.
//
// PDF is generated natively (selectable text) so it's light and reliable — no
// DOM rasterization. Layout mirrors the existing medical-note letterhead.

import { jsPDF } from 'jspdf'
import JSZip from 'jszip'
import { casesAPI, filesAPI } from '../services/api'
import { ORELLA_WORDMARK_PNG } from '../assets/logoPng'
import { generateAgreementPDF } from './agreementTemplate'
import formatAddress from './formatAddress'
import {
  getPatientName, formatDob, calculateAge, formatAllergies,
  formatMedications, formatConditions, formatSurgeries, formatInsurance,
} from './profileFormat'
import { parseDate } from './dateUtils'
import { readAvsText } from './avsText' // PROD-134 gamma: the one AVS reader
import { recordsVisitNotes } from './signedVisit' // PROD-1077: per-visit notes, a held one never printed
import { currentEncounterNumber } from '../utils/caseEncounters'
import { formatSex } from './formatSex'
import { gatherUploadedRecords, excludedFilesText, EXCLUDED_FILE_NAME } from './recordsUploads'

const NAVY = [21, 53, 79]
const BLUE = [70, 168, 249]
const GRAY = [100, 116, 139]
const LIGHT = [148, 163, 184]
const GREEN = [102, 209, 112]
const TINT = [205, 219, 234]

const PAGE_W = 612
const PAGE_H = 792
const M = 54
const CONTENT_W = PAGE_W - M * 2

// ★ v242.146.0 — through the calendar's owner. A date-only value built with a
//   bare `new Date(...)` is UTC midnight and renders as the previous day west
//   of UTC; `parseDate` builds it at local noon.
const fmtDate = (d) => {
  if (!d) return ''
  const dt = parseDate(d)
  if (!dt) return String(d)
  try {
    return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  } catch { return String(d) }
}

function isStoragePath(v) {
  return typeof v === 'string' && v &&
    !v.startsWith('data:') && !v.startsWith('http') &&
    !v.startsWith('blob:') && !v.startsWith('/') && v.includes('/')
}

function fileNameFromPath(p, fallback) {
  if (!p) return fallback
  const base = String(p).split('/').pop() || fallback
  return base.replace(/[^\w.\-() ]/g, '_')
}

// ── jsPDF flow helpers ──────────────────────────────────────────────
function ensureSpace(doc, state, need) {
  if (state.y + need > PAGE_H - M) {
    doc.addPage()
    state.y = M
    return true
  }
  return false
}

function addParagraph(doc, state, text, opts = {}) {
  const size = opts.size || 10
  const color = opts.color || [30, 41, 59]
  const lineH = opts.lineH || size * 1.45
  doc.setFont('helvetica', opts.bold ? 'bold' : 'normal')
  doc.setFontSize(size)
  doc.setTextColor(...color)
  const clean = String(text == null ? '' : text)
    .replace(/\r/g, '')
    .replace(/^#+\s?/gm, '')
    .replace(/\*\*/g, '')
  const lines = doc.splitTextToSize(clean || '—', CONTENT_W)
  for (const line of lines) {
    ensureSpace(doc, state, lineH)
    doc.text(line, M, state.y)
    state.y += lineH
  }
}

function sectionHeader(doc, state, title) {
  ensureSpace(doc, state, 28)
  state.y += 8
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(...NAVY)
  doc.text(String(title).toUpperCase(), M, state.y)
  state.y += 4
  doc.setDrawColor(...NAVY)
  doc.setLineWidth(1.2)
  doc.line(M, state.y, PAGE_W - M, state.y)
  state.y += 12
}

function letterhead(doc, state, rightLines = []) {
  state.y = M
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.setTextColor(...NAVY)
  doc.text('ORELLA HEALTH', M, state.y + 4)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(...LIGHT)
  doc.text('COMPREHENSIVE CARE PLATFORM', M, state.y + 16)
  doc.setFontSize(9)
  doc.setTextColor(...GRAY)
  let ry = state.y
  for (const ln of rightLines) {
    doc.text(String(ln), PAGE_W - M, ry, { align: 'right' })
    ry += 12
  }
  state.y += 30
  doc.setDrawColor(...NAVY)
  doc.setLineWidth(2)
  doc.line(M, state.y, PAGE_W - M, state.y)
  state.y += 18
}

function keyValueGrid(doc, state, rows) {
  doc.setFontSize(10)
  for (const [k, v] of rows) {
    ensureSpace(doc, state, 16)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...NAVY)
    doc.text(`${k}:`, M, state.y)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(30, 41, 59)
    const vlines = doc.splitTextToSize(String(v || '—'), CONTENT_W - 150)
    doc.text(vlines, M + 150, state.y)
    state.y += Math.max(16, vlines.length * 14)
  }
}

// ── Cover page (security-tinted confidential cover) ─────────────────
function addCover(doc, meta) {
  // security cross-hatch tint
  doc.setDrawColor(...TINT)
  doc.setLineWidth(0.4)
  for (let i = -PAGE_H; i < PAGE_W; i += 12) {
    doc.line(i, 0, i + PAGE_H, PAGE_H)
    doc.line(i, PAGE_H, i + PAGE_H, 0)
  }
  // frame
  doc.setDrawColor(...NAVY)
  doc.setLineWidth(2.5)
  doc.rect(22, 22, PAGE_W - 44, PAGE_H - 44)
  doc.setLineWidth(0.6)
  doc.rect(30, 30, PAGE_W - 60, PAGE_H - 60)

  const cx = PAGE_W / 2
  // wordmark logo + title
  // Brand wordmark as a centered PNG (transparent over the hatch). Falls back
  // to the text wordmark if the image fails to decode for any reason.
  const LW = 150
  const LH = LW * (170 / 600) // preserve wordmark aspect ratio
  try {
    doc.addImage(ORELLA_WORDMARK_PNG, 'PNG', cx - LW / 2, 116, LW, LH)
  } catch {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(30)
    doc.setTextColor(...BLUE)
    doc.text('orella', cx, 150, { align: 'center' })
  }
  doc.setDrawColor(...BLUE); doc.setLineWidth(1)
  doc.line(cx - 60, 172, cx + 60, 172)
  doc.setFontSize(22)
  doc.setTextColor(...NAVY)
  doc.text('Personal Health Record', cx, 215, { align: 'center' })
  doc.text('Export Package', cx, 245, { align: 'center' })

  // confidential pill
  doc.setFillColor(...NAVY)
  doc.roundedRect(cx - 150, 270, 300, 26, 13, 13, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(255, 255, 255)
  doc.text('CONFIDENTIAL · PHI ENCLOSED', cx, 287, { align: 'center' })

  // identity window
  const bx = M + 6, bw = CONTENT_W - 12, by = 320, bh = 150
  doc.setFillColor(255, 255, 255)
  doc.setDrawColor(...NAVY); doc.setLineWidth(0.9)
  doc.roundedRect(bx, by, bw, bh, 12, 12, 'FD')
  const rows = [
    ['PATIENT', meta.patient],
    ['ORELLA MEMBER ID', meta.memberId || '—'],
    ['DATE PREPARED', meta.date],
    ['RECORDS INCLUDED', meta.recordsLine],
  ]
  let ry = by + 30
  for (const [k, v] of rows) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...LIGHT)
    doc.text(k, bx + 22, ry)
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...NAVY)
    doc.text(String(v), bx + bw - 22, ry, { align: 'right' })
    ry += 34
  }

  // contents index
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...NAVY)
  doc.text('Contents of this package', M + 6, 510)
  const items = [
    'Cover sheet',
    'Face sheet — demographics, insurance & medical profile',
    'Patient care agreements',
    'Consult notes',
    'After-visit summaries (AVS)',
    'Uploaded records & documents',
  ]
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(30, 41, 59)
  let iy = 532
  for (const it of items) {
    doc.setFillColor(233, 248, 255); doc.setDrawColor(...BLUE); doc.setLineWidth(0.8)
    doc.roundedRect(M + 6, iy - 9, 13, 13, 2, 2, 'FD')
    doc.text(it, M + 28, iy)
    iy += 24
  }

  // footer notice
  doc.setDrawColor(...NAVY); doc.setLineWidth(0.5)
  doc.line(M + 6, PAGE_H - 96, PAGE_W - M - 6, PAGE_H - 96)
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY)
  doc.text(
    'This package contains protected health information (PHI). Handle and store it in accordance with HIPAA. If you received this in error, delete it and notify Orella Health.',
    M + 6, PAGE_H - 78, { maxWidth: CONTENT_W - 12 }
  )
}

// ── Face sheet ──────────────────────────────────────────────────────
function addFaceSheet(doc, state, profile, user, meta) {
  doc.addPage()
  letterhead(doc, state, ['Face sheet', meta.date])
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...NAVY)
  ensureSpace(doc, state, 22)
  doc.text('Patient face sheet', M, state.y); state.y += 16

  sectionHeader(doc, state, 'Demographics')
  keyValueGrid(doc, state, [
    ['Patient', meta.patient],
    ['Date of birth', formatDob(profile?.identification?.dob)],
    ['Age / Sex', `${calculateAge(profile?.identification?.dob)} / ${(formatSex(profile?.identification?.sex) || '—')}`],
    ['Member ID', meta.memberId || '—'],
    ['Phone', user?.phone || profile?.identification?.phone || '—'],
    ['Email', user?.email || profile?.email || '—'],
  ])

  sectionHeader(doc, state, 'Insurance')
  addParagraph(doc, state, formatInsurance(profile))

  sectionHeader(doc, state, 'Allergies')
  addParagraph(doc, state, formatAllergies(profile))

  sectionHeader(doc, state, 'Medical conditions')
  addParagraph(doc, state, formatConditions(profile))

  sectionHeader(doc, state, 'Surgical history')
  addParagraph(doc, state, formatSurgeries(profile))

  sectionHeader(doc, state, 'Medications')
  addParagraph(doc, state, formatMedications(profile))
}

// ── Per-visit record ────────────────────────────────────────────────
// ★ P2 (TEST 2.06): the finalize envelope this can carry — `{consultNote,
//   avsContent, billingCodes, cptCodes, ...}` — reaches the patient app (P4
//   Q9, filed). `avsTextFrom` is the ONE place that unwraps it: a JSON
//   envelope gives up only its `avsContent`, and anything else (plain text,
//   or nothing) passes through unchanged. It never returns the raw envelope
//   string, so a billing code embedded in it can never reach a patient screen
//   or PDF through this path.
function avsTextFrom(raw) {
  if (typeof raw !== 'string' || !raw) return ''
  let parsed = null
  try { const p = JSON.parse(raw); if (p && typeof p === 'object') parsed = p } catch { /* plain text */ }
  return parsed ? (parsed.avsContent || '') : raw
}

function extractVisit(c) {
  // PROD-134 gamma: the AVS through the one reader (stored cases.avs first).
  // ★ PROD-1077: the consult notes come per signed visit (utils/signedVisit.js recordsVisitNotes);
  //   a held visit is marked and its text is never taken. Only a case with no signed visit falls
  //   back to the legacy case-level note (the finalize envelope, then the column).
  const rawAvs = c.avsContent || c.afterVisitSummary || c.diagnose?.avsContent || ''
  let parsed = null
  if (typeof rawAvs === 'string' && rawAvs) { try { const p = JSON.parse(rawAvs); if (p && typeof p === 'object') parsed = p } catch { /* plain text */ } }
  const legacyNote = parsed?.consultNote || c.diagnose?.consultNote || c.consultNote || ''
  const notes = recordsVisitNotes(c, legacyNote, parsed) // PROD-1079: never an unsigned draft
  const avs = avsTextFrom(readAvsText(c, currentEncounterNumber(c)))
  return { notes, avs }
}

// ★ PROD-1077 (PENDING COPY APPROVAL, Alex): printed in place of a held visit's note. English,
//   like the rest of this PDF.
export const HELD_NOTE_LINE = 'This consult note is being held by your physician. Contact the office for a copy.'

// The paragraphs of the "Consult note" section, in order: one per signed visit (a held visit
// gives HELD_NOTE_LINE, never its text), each led by its visit date when the case has more than
// one; nothing at all -> the existing "No consult note on file" line.
export function consultNoteParagraphs(notes, fmt) {
  if (!Array.isArray(notes) || notes.length === 0) return ['No consult note on file for this visit.']
  const out = []
  for (const v of notes) {
    if (notes.length > 1 && v.date) out.push(fmt(v.date))
    out.push(v.held ? HELD_NOTE_LINE : v.note)
  }
  return out
}

// ── Single-encounter AVS PDF (TEST 2.06 — the Deliver "Download After Visit
//    Summary" button). Built from the same letterhead/sectionHeader/
//    addParagraph flow as the full records package, reusing its existing
//    "After-visit summary" label — no new user-facing string.
export function generateAvsPdf({ text, title, dateStr, patientName, compress = true }) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter', compress })
  const state = { y: M }
  letterhead(doc, state, ['After-visit summary', title, dateStr].filter(Boolean))
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...NAVY)
  ensureSpace(doc, state, 22)
  doc.text(patientName || 'Patient', M, state.y); state.y += 16
  sectionHeader(doc, state, 'After-visit summary')
  addParagraph(doc, state, text || 'No after-visit summary on file for this visit.')

  const pageCount = doc.getNumberOfPages()
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...LIGHT)
    doc.text(`Orella Health · Confidential · Page ${p} of ${pageCount}`, PAGE_W / 2, PAGE_H - 24, { align: 'center' })
  }
  return doc.output('blob')
}

function addVisit(doc, state, c, meta) {
  doc.addPage()
  const { notes, avs } = extractVisit(c)
  // PROD-957: the page is dated by its latest signed visit (get_case sends no top-level finalizedAt).
  const lastSigned = notes.filter((v) => v.date).pop()
  const dateStr = fmtDate(lastSigned?.date || c.finalizedAt || c.updatedAt || c.createdAt)
  letterhead(doc, state, ['Visit record', `Case ${c.caseNumber || ''}`, dateStr])
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...NAVY)
  ensureSpace(doc, state, 22)
  doc.text(`Visit — ${c.focusArea || c.specialty || 'Case'}`, M, state.y); state.y += 16

  keyValueGrid(doc, state, [
    ['Patient', meta.patient],
    ['Date', dateStr || '—'],
    ['Concern', c.focusArea || c.specialty || '—'],
  ])

  sectionHeader(doc, state, 'Consult note')
  for (const p of consultNoteParagraphs(notes, fmtDate)) addParagraph(doc, state, p)
  sectionHeader(doc, state, 'After-visit summary')
  addParagraph(doc, state, avs || 'No after-visit summary on file for this visit.')
}

// ── Upload gathering ────────────────────────────────────────────────
function collectUploadPaths(c) {
  const out = []
  const buckets = [
    c.detect?.photos, c.detect?.documents, c.documents, c.photos, c.uploads,
  ]
  for (const arr of buckets) {
    if (!Array.isArray(arr)) continue
    for (const item of arr) {
      const path = item?.storagePath || (isStoragePath(item?.url) ? item.url : null) ||
        (isStoragePath(item?.storageUrl) ? item.storageUrl : null) || (isStoragePath(item) ? item : null)
      if (path) out.push({ path, name: item?.name || item?.fileName || fileNameFromPath(path, 'file'), caseNumber: c.caseNumber })
    }
  }
  return out
}

// Signed Care Agreement → faithful standalone HTML (same template the
// CareAgreementScreen renders). Returns null if the case has no signed agreement.
function buildAgreementHTML(c, profile) {
  const agreement = c?.patientAgreement || c?.patient_agreement
  if (!agreement?.signature || !(agreement.signedAt || agreement.signed_at)) return null
  // ★ v242.146.0 — this round-tripped through toISOString, which converts to UTC
  //   before slicing the date off, so a local evening timestamp printed as the
  //   NEXT day and a date-only value as the previous one. NEVER BUILD A DATE
  //   FROM toISOString (1dc(l)).
  const fmtD = (iso) => {
    if (!iso) return 'N/A'
    const d = parseDate(iso)
    if (!d) return String(iso)
    const p = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
  }
  const fmtDT = (iso) => {
    if (!iso) return 'N/A'
    try {
      const d = parseDate(iso)
      if (!d) return String(iso)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } catch { return iso }
  }
  const patientName = `${profile?.firstName || profile?.identification?.firstName || ''} ${profile?.lastName || profile?.identification?.lastName || ''}`.trim() || 'Patient'
  const d = c.doctor || {}
  const doctorName = c.doctorName || c.doctor_name || ''
  const parts = doctorName.replace(/^Dr\.?\s*/i, '').split(' ')
  const fullAddress = [
    formatAddress(d.practice_address),
    [d.practice_city, d.practice_state].filter(Boolean).join(', '),
    d.practice_zip,
  ].filter(Boolean).join(', ')
  const doctor = {
    firstName: d.first_name || parts[0] || '',
    lastName: d.last_name || parts.slice(1).join(' ') || '',
    credentials: d.credentials || 'MD',
    practiceName: c.practiceName || c.practice_name || d.practice_name || '',
    practiceAddress: fullAddress,
    practicePhone: d.practice_phone || d.phone || '',
    practiceFax: d.practice_fax || '',
    practiceEmail: d.practice_email || '',
    practiceState: d.practice_state || '',
    practiceCity: d.practice_city || '',
    // TF b34 #8 — see AgreementPreviewModal. Absent today; falls back to $99/$249.
    // ★ v242.151.0 AGREEMENT-FEE-FIELD-1 — the names the MD side actually
    //   writes. `virtual_consult_fee` / `in_office_consult_fee` exist nowhere
    //   in the API or the panel, so this always fell through to the template's
    //   $99/$249 — which happen to be the same numbers as the stored rates,
    //   which is why the document looked correct while reading nothing.
    virtualFee: d.self_pay_virtual_rate != null ? d.self_pay_virtual_rate : d.selfPayVirtualRate,
    inOfficeFee: d.self_pay_in_office_rate != null ? d.self_pay_in_office_rate : d.selfPayInOfficeRate,
    // v242.239.0 (TEST 2.21): only a v2 record prints these fees; v3 carries no pricing, v1 its own.
  }
  try {
    return generateAgreementPDF({
      patientName,
      patientDOB: fmtD(profile?.identification?.dob || profile?.dob || profile?.identification?.dateOfBirth),
      patientSex: formatSex(profile?.gender || profile?.sex || profile?.identification?.sex) || 'N/A',
      patientId: profile?.id?.slice(-8) || c.id?.slice(-8) || 'N/A',
      caseId: c.id?.slice(-12) || 'N/A',
      signatureData: agreement.signature || '',
      signedDate: fmtDT(agreement.signedAt || agreement.signed_at),
      doctor,
      agreementVersion: agreement.agreementVersion,
      // P2 repair 2 (NIT-1): V3 only when the record says it signed the hardcoded V3 text.
      hardcodedTemplate: agreement.hardcodedTemplate,
      title: agreement.title,
      sections: agreement.sections,
      effectiveDate: agreement.effectiveDate || agreement.effective_date,
    })
  } catch {
    return null
  }
}

// ── Public entry point ──────────────────────────────────────────────
export async function generateRecordsPackage({ profile, user, onProgress = () => {} }) {
  onProgress('Loading your records…', 8)
  let caseList = []
  try {
    const res = await casesAPI.list()
    caseList = Array.isArray(res) ? res : (res?.cases || [])
  } catch (e) {
    throw new Error('Could not load your records. Please try again.')
  }

  // Full case data (notes/AVS/uploads live on the detail record).
  const fullCases = []
  for (let i = 0; i < caseList.length; i++) {
    const id = caseList[i]?.id || caseList[i]?.caseId
    if (!id) continue
    try {
      const full = await casesAPI.get(id)
      fullCases.push(full?.case || full)
    } catch { /* skip a case that fails to load */ }
    onProgress('Gathering visit records…', 10 + Math.round((i / Math.max(caseList.length, 1)) * 35))
  }
  // Visits worth including: finalized / with a note.
  const visits = fullCases.filter((c) => c && (c.finalizedAt || c.status === 'completed' || extractVisit(c).notes.length > 0))

  onProgress('Building your document…', 50)
  const meta = {
    patient: getPatientName(profile, user),
    memberId: user?.orellaMemberId || profile?.orellaMemberId || profile?.identification?.orellaMemberId,
    date: fmtDate(new Date().toISOString()),
    recordsLine: `${visits.length} visit${visits.length === 1 ? '' : 's'}`,
  }

  const doc = new jsPDF({ unit: 'pt', format: 'letter' })
  const state = { y: M }
  addCover(doc, meta)
  addFaceSheet(doc, state, profile, user, meta)
  for (const c of visits) {
    try { addVisit(doc, state, c, meta) } catch { /* skip a malformed visit */ }
  }

  // page footers
  const pageCount = doc.getNumberOfPages()
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...LIGHT)
    doc.text(`Orella Health · Confidential · Page ${p} of ${pageCount}`, PAGE_W / 2, PAGE_H - 24, { align: 'center' })
  }

  const pdfBlob = doc.output('blob')

  // Uploads
  onProgress('Collecting uploaded records…', 70)
  const uploads = []
  for (const c of fullCases) uploads.push(...collectUploadPaths(c))
  const zip = new JSZip()
  zip.file('Orella_Health_Records.pdf', pdfBlob)

  // ★★ P2 Release 3 (PROD-167): the batch now says which paths it REFUSED (`denied`), and
  //   every file that is not in the package is named in EXCLUDED_FILES.txt with its reason.
  //   Before, a refused file and a missing one were both skipped in silence, so an export
  //   that had lost files read exactly like a complete one. See utils/recordsUploads.js.
  let excluded = []
  if (uploads.length) {
    const folder = zip.folder('uploaded-records')
    excluded = await gatherUploadedRecords({
      uploads,
      signBatch: (batch) => filesAPI.getSignedUrlsWithDenied(batch),
      fetchFile: (url) => fetch(url),
      addFile: (name, buf) => folder.file(name, buf),
      onProgress: (done, total) => onProgress('Collecting uploaded records…', 70 + Math.round((done / total) * 20)),
    })
    const excludedText = excludedFilesText(excluded)
    if (excludedText) zip.file(EXCLUDED_FILE_NAME, excludedText)
  }

  // Signed care agreements (faithful standalone HTML, printable to PDF).
  let agreementCount = 0
  let agFolder = null
  for (const c of fullCases) {
    const html = buildAgreementHTML(c, profile)
    if (!html) continue
    if (!agFolder) agFolder = zip.folder('care-agreements')
    const label = c.caseNumber ? `case-${c.caseNumber}` : (c.id ? c.id.slice(-6) : `${agreementCount + 1}`)
    agFolder.file(`Care_Agreement_${label}.html`, html)
    agreementCount++
  }

  onProgress('Compressing package…', 92)
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } })
  onProgress('Done', 100)

  const stamp = new Date().toISOString().slice(0, 10)
  return { blob, filename: `Orella_Health_Records_${stamp}.zip`, excluded }
}
